import { AssemblyItem } from '@/assembly/api/types';
import {
  KeyboardArrowDown,
  KeyboardArrowUp,
} from '@/constants/hardfin-standard-icons';
import { formatNumber } from '@/utilities/number';
import { pluralize } from '@/utilities/pluralize';
import {
  Box,
  Collapse,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useState } from 'react';

interface PartRowProps {
  part: AssemblyItem;
}

function PartRow({ part }: PartRowProps) {
  const [open, setOpen] = useState(false);
  const hasSerials = part.serials;

  return (
    <>
      <TableRow>
        <TableCell>
          {hasSerials && (
            <IconButton size="small" onClick={() => setOpen(!open)}>
              {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>
          <Typography variant="body2">{part.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {part.sku}
          </Typography>
        </TableCell>
        <TableCell>{formatNumber(part.actualQuantity)}</TableCell>
        <TableCell>
          {hasSerials ? (
            <Typography variant="body2">
              {pluralize(part.serials!.length, { singular: 'serial number' })}
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">
              Not serialized
            </Typography>
          )}
        </TableCell>
      </TableRow>
      {hasSerials && (
        <TableRow>
          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={4}>
            <Collapse in={open} timeout="auto" unmountOnExit>
              <Box sx={{ margin: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Serial numbers
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableBody>
                      {part.serials!.map((serial: string, index: number) => (
                        <TableRow key={index}>
                          <TableCell component="th" scope="row">
                            {serial}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </Collapse>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

interface Props {
  partsUsed: AssemblyItem[];
}

export function AssemblyPartsUsed({ partsUsed }: Props) {
  const totalParts = partsUsed.reduce(
    (total, part) => total + part.actualQuantity,
    0
  );

  return (
    <Paper variant="outlined">
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle1" gutterBottom>
          Total parts: {totalParts}
        </Typography>
      </Box>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="5%"></TableCell>
              <TableCell width="50%">Part</TableCell>
              <TableCell width="15%">Quantity</TableCell>
              <TableCell width="30%">Serial numbers</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {partsUsed.map((part, index) => (
              <PartRow key={index} part={part} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}
